import React from 'react';
import styled from 'styled-components';

const PrivacyEn: React.FC = () => {

  return (
    <>
      <Section>
        <Title>Privacy Policy</Title>
        <Text>
          Architrave (“Company”, “we”, “us”, or “our”) explains how we collect, use, share, retain, and protect your personal information. This Policy is prepared in accordance with the Personal Information Protection Act of the Republic of Korea (“PIPA”).
        </Text>
        <Text>Effective date: August 12, 2025.</Text>
      </Section>

      <Section>
        <SmallTitle>Why we use personal information</SmallTitle>
        <Text>
          We only use personal information for the purposes below. If we ever need to use it for a new purpose, we’ll ask for your consent first.
          <List2>
            <li>
              <Strong>Account sign-up & management</Strong>
              <List>
                <li>Verify sign-up intent, identity/authentication, and email verification</li>
                <li>Confirm you are 14+ and collect required consents to the Terms and this Policy</li>
              </List>
            </li>
            <li>
              <Strong>Service delivery & operations</Strong>
              <List>
                <li>Login, access to features/content, and subscription billing</li>
                <li>Analytics to improve performance and plan future features</li>
              </List>
            </li>
            <li>
              <Strong>Notices & safety communications</Strong>
              <List>
                <li>Service changes, updates to terms, and security/privacy notices</li>
              </List>
            </li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Personal information we collect</SmallTitle>
        <Text>
          <List>
            <li><Strong>Required:</Strong> email address, password, display name (username)</li>
            <li><Strong>Automatically collected:</Strong> IP address, browser/device info, access timestamps, service usage logs</li>
            <li><Strong>Other:</Strong> email verification codes created during sign-up</li>
          </List>
        </Text>
      </Section>

      <Section>
        <SmallTitle>How we collect it</SmallTitle>
        <Text>
          <List>
            <li>Information you enter during sign-up</li>
            <li>Information generated during the email verification process</li>
            <li>Information automatically created while you use the service (e.g., access logs)</li>
          </List>
        </Text>
      </Section>

      <Section>
        <SmallTitle>How long we keep information</SmallTitle>
        <Text>
          We delete personal information when it’s no longer needed or when you delete your account. Some records must be kept for the periods required under Korean law:
        </Text>
        <Table>
          <thead>
            <tr>
              <th>Record type</th>
              <th>Legal basis (Korea)</th>
              <th>Retention</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Advertising/display records</td>
              <td>E-Commerce Act (Enforcement Decree)</td>
              <td>6 months</td>
            </tr>
            <tr>
              <td>Contracts & withdrawal/cancellation</td>
              <td>E-Commerce Act</td>
              <td>5 years</td>
            </tr>
            <tr>
              <td>Payments & supply of goods/services</td>
              <td>E-Commerce Act</td>
              <td>5 years</td>
            </tr>
            <tr>
              <td>Consumer complaints/disputes</td>
              <td>E-Commerce Act</td>
              <td>3 years</td>
            </tr>
            <tr>
              <td>Electronic financial transactions</td>
              <td>Electronic Financial Transactions Act</td>
              <td>5 years</td>
            </tr>
            <tr>
              <td>Access logs and related communications data</td>
              <td>Protection of Communications Secrets Act</td>
              <td>3 months</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <SmallTitle>Sharing with third parties</SmallTitle>
        <Text>
          We do <Strong>not</Strong> sell or share your personal information with third parties, except where required by law or where you separately consent. Payment processing via Stripe is handled as an entrusted processor (see below) and is not considered third-party “provision.”
        </Text>
      </Section>

      <Section>
        <SmallTitle>Processors who help us run the service</SmallTitle>
        <Table>
          <thead>
            <tr>
              <th>Processor</th>
              <th>Purpose</th>
              <th>Retention</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Stripe, Inc. (including Polar.sh)</td>
              <td>Payment processing, settlement, and billing-related customer identification and transaction handling</td>
              <td>Until account deletion or termination of the processing agreement</td>
            </tr>
            <tr>
              <td>Amazon Web Services, Inc. and Amazon Web Services Korea LLC</td>
              <td>Cloud infrastructure provisioning and operations (including application servers, databases, storage, backup and recovery, monitoring, security), and content delivery network (CDN, Amazon CloudFront) operation and performance/security log processing</td>
              <td>Until account deletion or termination of the processing agreement</td>
            </tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <SmallTitle>Cross-border transfers</SmallTitle>
        <Text>
          <List2>
            <li>
              <Strong>Stripe in the U.S.</Strong>
              <List>
                <li><Strong>Recipient:</Strong> Stripe, Inc. (United States)</li>
                <li><Strong>Items:</Strong> email address and payment-related identifiers necessary for billing</li>
                <li><Strong>Timing:</Strong> when you initiate a payment</li>
                <li><Strong>Method:</Strong> secure network transmission</li>
                <li><Strong>Retention:</Strong> until the purpose is fulfilled or your account is deleted</li>
                <li><Strong>Opt-out & impact:</Strong> You can contact us at <Strong>architrave2025@gmail.com</Strong> to refuse cross-border transfers. However, payments will not work, which may limit your ability to use subscription features.</li>
                <li>
                  Stripe’s Privacy Policy: <Link href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</Link>
                </li>
              </List>
            </li>
            <li>
              <Strong>Amazon Web Services (Global)</Strong>
              <List>
                <li><Strong>Recipient:</Strong> Amazon Web Services, Inc. (global edge locations)</li>
                <li><Strong>Items:</Strong> IP address, request metadata (e.g., browser type, OS, access logs), and account identifiers (if applicable)</li>
                <li><Strong>Timing:</Strong> when you access or interact with the service</li>
                <li><Strong>Method:</Strong> secure network transmission via Amazon CloudFront (CDN)</li>
                <li><Strong>Retention:</Strong> until the purpose is fulfilled or your account is deleted</li>
                <li><Strong>Opt-out & impact:</Strong> Due to the nature of content delivery networks (CDNs), you cannot opt out of this transfer unless you stop using the service. CDN delivery is essential for ensuring performance, availability, and security.</li>
                <li>
                  AWS Privacy Policy: <Link href="https://aws.amazon.com/privacy" target="_blank" rel="noopener noreferrer">https://aws.amazon.com/privacy</Link>
                </li>
              </List>
            </li>
          </List2>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Your privacy rights</SmallTitle>
        <Text>
          You can, at any time:
          <List2>
            <li>request access to your data;</li>
            <li>request correction or deletion; and</li>
            <li>request that we suspend processing.</li>
          </List2>
          To exercise these rights, email us at <Strong>architrave2025@gmail.com</Strong>. You can also view or withdraw the consents you gave at sign-up (e.g., “I am 14 or older,” Terms and Privacy consent) at any time.
        </Text>
      </Section>

      <Section>
        <SmallTitle>How we delete information</SmallTitle>
        <Text>
          <List>
            <li><Strong>Electronic files:</Strong> securely and irreversibly deleted</li>
            <li><Strong>Paper records:</Strong> shredded or incinerated</li>
          </List>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Cookies</SmallTitle>
        <Text>
          We may use cookies to operate and improve the service. You can disable cookies in your browser settings (note: certain features may not function properly if cookies are disabled).
        </Text>
      </Section>

      <Section>
        <SmallTitle>How to contact us</SmallTitle>
        <Text>
          <List>
            <li><Strong>Name:</Strong> Youngdong Kim</li>
            <li><Strong>Title:</Strong> Representative (Privacy Officer)</li>
            <li><Strong>Phone:</Strong> +82-10-5049-5056</li>
            <li><Strong>Email:</Strong> architrave2025@gmail.com</li>
            <li><Strong>Department:</Strong> N/A (managed directly by the Representative as a one-person business)</li>
          </List>
        </Text>
      </Section>

      <Section>
        <SmallTitle>Changes to this Policy</SmallTitle>
        <Text>
          If we make changes due to law, policy, or service updates, we’ll provide advance notice. For material changes, we’ll give at least 30 days’ prior notice.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Children</SmallTitle>
        <Text>
          We do not knowingly collect personal information from children under 14. During sign-up, we obtain explicit confirmation that you are 14 years of age or older.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Security</SmallTitle>
        <Text>
          We apply reasonable technical and organizational safeguards, including least-privilege access, password encryption, regular backups and updates, security protections against intrusion/malware, and prompt breach notifications as required by law.
        </Text>
      </Section>

      <Section>
        <SmallTitle>Remedies</SmallTitle>
        <Text>
          If you believe your privacy rights have been infringed, you may seek counseling or dispute resolution from the following organizations in Korea:
          <List>
            <li>Personal Information Dispute Mediation Committee: <Link href="https://www.kopico.go.kr" target="_blank" rel="noopener noreferrer">https://www.kopico.go.kr</Link> / +82-1833-6972</li>
            <li>Personal Information Infringement Report Center (KISA): <Link href="https://privacy.kisa.or.kr" target="_blank" rel="noopener noreferrer">https://privacy.kisa.or.kr</Link> / 118</li>
            <li>Supreme Prosecutors’ Office - Cybercrime: <Link href="https://www.spo.go.kr" target="_blank" rel="noopener noreferrer">https://www.spo.go.kr</Link> / 1301</li>
            <li>Korean National Police Agency - Cyber Bureau: <Link href="https://ecrm.cyber.go.kr" target="_blank" rel="noopener noreferrer">https://ecrm.cyber.go.kr</Link></li>
          </List>
        </Text>
      </Section>

      <Text>Effective date: August 12, 2025.</Text>

    </>
  );
};

export default PrivacyEn;

const Title = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_02_1};
  margin-bottom: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

const SmallTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_01};
  ${({ theme }) => theme.typography.Body_03_1};
  margin-bottom: 4px;
`;

const Text = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  line-height: 1.4;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-decoration: underline;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  th, td {
    border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
    padding: 4px 4px;
    ${({ theme }) => theme.typography.Body_04};
    color: ${({ theme }) => theme.colors.color_Gray_03};
    text-align: left;
    line-height: 1.4;
  }
  th {
    background: ${({ theme }) => theme.colors.color_Gray_07};
    color: ${({ theme }) => theme.colors.color_Gray_01};
  }
`;

const List = styled.ul`
  list-style: disc;
  padding-left: 14px;
`;

const List2 = styled.ol`
  list-style: decimal;
  padding-left: 14px;
  li {
    margin-bottom: 4px;
  }
`;

const Strong = styled.strong`
  font-weight: ${props => props.theme.fontWeight.medium};
`;